import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /**
     * Load functional usage for the current space cluster and scenario context.
     */
    loadForCurrentEntity: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        request: ({ state: { editingFunctionalUsage } }) => ({
          ...State.INITIAL,
          editingFunctionalUsage,
          isLoading: true,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
      },
    }),
    /**
     * Load functional usage for the supplied space cluster and scenario context.
     */
    load: store.actions.request({
      input: store.actions.Data.asIs<{
        context: ents.core.admin.Context.ForScenario
        clusterId: ents.extra.space.Cluster.Id
      }>(),
      output: store.actions.Data.asIs<ents.extra.space.Functionalization[]>(),
      reducers: {
        request: ({ state }) => ({
          ...state,
          isLoading: true,
        }),
        success: ({ state, data: { response } }) => ({
          ...state,
          error: undefined,
          functionalUsage: response,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
      },
    }),
    /** Initialize editing state with a copy of current functional usage */
    beginUpdating: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        editingFunctionalUsage: state.functionalUsage
          ? [...state.functionalUsage]
          : [],
      }),
    }),
    /** Discard the editing state */
    discard: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }) => ({
        ...state,
        editingFunctionalUsage: undefined,
      }),
    }),
    /**
     * Update a specific functionalization's percentage or add it if it doesn't exist
     */
    updateFunctionalization: store.actions.standalone({
      data: store.actions.Data.asIs<ents.extra.space.Functionalization>(),
      reducer: ({ state, data }) => State.updateFunctionalization(state, data),
    }),
    /**
     * Submit functional usage edits for a cluster
     */
    applyUpdates: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<ents.extra.space.Functionalization[]>(),
      reducers: {
        request: ({ state }) => ({
          ...state,
          isLoading: true,
        }),
        success: ({ state, data: { response } }) => ({
          ...state,
          error: undefined,
          functionalUsage: response,
          editingFunctionalUsage: undefined,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: ({ state }) => ({
          ...state,
          isLoading: false,
        }),
      },
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
